import { cacheOperatorRows, readOperatorRows } from "@/lib/operator-drafts";
import { useOperatorDraft } from "@/hooks/useOperatorDraft";
import { queueScope } from "@/lib/offline-queue";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOffline } from "@/contexts/OfflineContext";
import { apiFetch } from "@/lib/api-fetch";
import { hapticSuccess } from "@/lib/native-init";
import {
  RotateCcw,
  Check,
  ArrowLeft,
  Droplets,
  Wrench,
  User,
  Search,
  Filter,
  ArrowUpDown,
  FolderTree,
  Plus,
  Camera,
} from "lucide-react";
import PhotoCapture from "@/components/PhotoCapture";

type WindrowOption = {
  id: number;
  row_id: string;
  name: string;
  status: string;
};

type TurnRecord = {
  id: number;
  windrow_id: number;
  windrow_name: string;
  windrow_row_id: string;
  turn_number: number;
  water_added: boolean;
  equipment: string | null;
  operator: string | null;
  photo_url: string | null;
  notes: string | null;
  turned_at: string;
};

type TurnSortBy = "turned_at" | "turn_number" | "windrow_name" | "operator" | "equipment";
type TurnGroupBy = "none" | "windrow" | "day" | "operator" | "equipment";

const EQUIPMENT_OPTIONS = [
  { label: "Windrow Turner", icon: "🔄" },
  { label: "Front Loader", icon: "🚜" },
  { label: "Backhoe", icon: "⛏️" },
  { label: "Excavator", icon: "🏗️" },
  { label: "Skid Steer", icon: "🚧" },
  { label: "Manual", icon: "🧑‍🔧" },
  { label: "Other", icon: "⚙️" },
];

function compareValues(a: string | number, b: string | number) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}

function toDayKey(dateString: string) {
  return dateString.slice(0, 10);
}

function formatDateTime(dateString: string) {
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function LogTurnPage() {
  const location = useLocation();
  const isOperatorView = location.pathname.startsWith("/app/operator");

  if (isOperatorView) {
    return <OperatorTurnLogger />;
  }

  return <ManagerTurnWorkspace />;
}

function ManagerTurnWorkspace() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedWindrow = searchParams.get("windrow");

  const [records, setRecords] = useState<TurnRecord[]>([]);
  const [windrows, setWindrows] = useState<WindrowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [windrowId, setWindrowId] = useState(preselectedWindrow || "");
  const [equipment, setEquipment] = useState("");
  const [waterAdded, setWaterAdded] = useState(false);
  const [operator, setOperator] = useState(user?.name || user?.email || "Manager");
  const [notes, setNotes] = useState("");

  const [search, setSearch] = useState("");
  const [windrowFilter, setWindrowFilter] = useState("all");
  const [waterFilter, setWaterFilter] = useState<"all" | "water" | "no_water">("all");
  const [sortBy, setSortBy] = useState<TurnSortBy>("turned_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [groupBy, setGroupBy] = useState<TurnGroupBy>("none");

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [turnsRes, windrowsRes] = await Promise.all([
        apiFetch("/api/turns", { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch("/api/windrows/active", { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (!turnsRes.ok) {
        throw new Error(`Failed to load turns (${turnsRes.status})`);
      }
      if (!windrowsRes.ok) {
        throw new Error(`Failed to load windrows (${windrowsRes.status})`);
      }

      const turnsData = await turnsRes.json();
      const windrowsData = await windrowsRes.json();
      const nextWindrows = windrowsData.windrows || [];

      setRecords(turnsData.turns || []);
      setWindrows(nextWindrows);

      if (!windrowId && !preselectedWindrow && nextWindrows.length > 0) {
        setWindrowId(String(nextWindrows[0].id));
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [token]);

  const stats = useMemo(() => {
    const total = records.length;
    const water = records.filter((r) => r.water_added).length;
    const uniqueWindrows = new Set(records.map((r) => r.windrow_id)).size;
    const todayKey = toDayKey(new Date().toISOString());
    const today = records.filter((r) => toDayKey(r.turned_at) === todayKey).length;
    return { total, water, uniqueWindrows, today };
  }, [records]);

  const sortedRows = useMemo(() => {
    const searchLower = search.trim().toLowerCase();

    const filtered = records.filter((record) => {
      if (windrowFilter !== "all" && String(record.windrow_id) !== windrowFilter) {
        return false;
      }
      if (waterFilter === "water" && !record.water_added) {
        return false;
      }
      if (waterFilter === "no_water" && record.water_added) {
        return false;
      }
      if (!searchLower) {
        return true;
      }

      const haystack = [
        record.windrow_name,
        record.windrow_row_id,
        record.equipment || "",
        record.operator || "",
        record.notes || "",
        String(record.turn_number),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(searchLower);
    });

    return [...filtered].sort((a, b) => {
      let left: string | number;
      let right: string | number;

      if (sortBy === "turned_at") {
        left = new Date(a.turned_at).getTime();
        right = new Date(b.turned_at).getTime();
      } else if (sortBy === "turn_number") {
        left = a.turn_number;
        right = b.turn_number;
      } else if (sortBy === "windrow_name") {
        left = `${a.windrow_name} ${a.windrow_row_id}`;
        right = `${b.windrow_name} ${b.windrow_row_id}`;
      } else if (sortBy === "equipment") {
        left = a.equipment || "";
        right = b.equipment || "";
      } else {
        left = a.operator || "";
        right = b.operator || "";
      }

      const value = compareValues(left, right);
      return sortDirection === "asc" ? value : -value;
    });
  }, [records, search, windrowFilter, waterFilter, sortBy, sortDirection]);

  const groupedRows = useMemo(() => {
    if (groupBy === "none") {
      return [{ key: "all", label: `All Records (${sortedRows.length})`, rows: sortedRows }];
    }

    const map = new Map<string, TurnRecord[]>();

    for (const row of sortedRows) {
      let key = "";
      if (groupBy === "windrow") {
        key = `${row.windrow_name} (${row.windrow_row_id})`;
      } else if (groupBy === "day") {
        key = toDayKey(row.turned_at);
      } else if (groupBy === "equipment") {
        key = row.equipment || "No equipment";
      } else {
        key = row.operator || "Unassigned";
      }

      const group = map.get(key);
      if (group) {
        group.push(row);
      } else {
        map.set(key, [row]);
      }
    }

    return Array.from(map.entries()).map(([key, rows]) => ({
      key,
      label: groupBy === "day" ? `${new Date(`${key}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} (${rows.length})` : `${key} (${rows.length})`,
      rows,
    }));
  }, [sortedRows, groupBy]);

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!windrowId) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await apiFetch(`/api/windrows/${windrowId}/turns`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          waterAdded,
          equipment: equipment || null,
          operator: operator || null,
          notes: notes || null,
          photoUrl: null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to record turn (${res.status})`);
      }

      setEquipment("");
      setNotes("");
      setWaterAdded(false);
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to record turn");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="heading-display text-3xl text-foreground">Turn Records</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manager table workspace for rapid turn logging and operational review.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Total Turns</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.total}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Turns With Water</p>
          <p className="stat-value text-3xl text-blue-600 dark:text-blue-400 mt-2">{stats.water}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Active Windrows</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.uniqueWindrows}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Turns Today</p>
          <p className="stat-value text-3xl text-foreground mt-2">{stats.today}</p>
        </div>
      </div>

      <form onSubmit={handleAddRecord} className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Plus size={16} className="text-blue-600" />
          Add Turn Record
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <select
            value={windrowId}
            onChange={(e) => setWindrowId(e.target.value)}
            className="lg:col-span-2 px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
            required
          >
            <option value="">Select windrow</option>
            {windrows.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.row_id})
              </option>
            ))}
          </select>

          <select
            value={equipment}
            onChange={(e) => setEquipment(e.target.value)}
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
          >
            <option value="">No equipment</option>
            {EQUIPMENT_OPTIONS.map((option) => (
              <option key={option.label} value={option.label}>
                {option.label}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={operator}
            onChange={(e) => setOperator(e.target.value)}
            placeholder="Operator"
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
          />

          <label className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-background border border-border rounded-lg text-sm">
            <input
              type="checkbox"
              checked={waterAdded}
              onChange={(e) => setWaterAdded(e.target.checked)}
              className="rounded"
            />
            Water Added
          </label>

          <button
            type="submit"
            disabled={submitting || !windrowId}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>

        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
        />
      </form>

      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <label className="lg:col-span-2 flex items-center gap-2 px-3 bg-background border border-border rounded-lg">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search windrow, equipment, operator..."
              className="w-full py-2 text-sm bg-transparent outline-none"
            />
          </label>

          <select
            value={windrowFilter}
            onChange={(e) => setWindrowFilter(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="all">All windrows</option>
            {windrows.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.row_id})
              </option>
            ))}
          </select>

          <select
            value={waterFilter}
            onChange={(e) => setWaterFilter(e.target.value as "all" | "water" | "no_water")}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="all">All water states</option>
            <option value="water">Water added</option>
            <option value="no_water">No water</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as TurnSortBy)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="turned_at">Sort by date</option>
            <option value="turn_number">Sort by turn #</option>
            <option value="windrow_name">Sort by windrow</option>
            <option value="operator">Sort by operator</option>
            <option value="equipment">Sort by equipment</option>
          </select>

          <button
            type="button"
            onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm inline-flex items-center justify-center gap-1"
          >
            <ArrowUpDown size={14} />
            {sortDirection === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <FolderTree size={14} className="text-muted-foreground" />
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as TurnGroupBy)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="none">No grouping</option>
            <option value="windrow">Group by windrow</option>
            <option value="day">Group by day</option>
            <option value="operator">Group by operator</option>
            <option value="equipment">Group by equipment</option>
          </select>
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Filter size={12} />
            {sortedRows.length} matching records
          </span>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading records...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600 dark:text-red-400">{error}</div>
        ) : sortedRows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No turn records found for current filters.</div>
        ) : (
          <div className="divide-y divide-border">
            {groupedRows.map((group) => (
              <section key={group.key} className="p-4">
                {groupBy !== "none" && (
                  <h3 className="text-sm font-semibold text-foreground mb-3">{group.label}</h3>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2.5">Turned At</th>
                        <th className="px-3 py-2.5">Windrow</th>
                        <th className="px-3 py-2.5">Turn #</th>
                        <th className="px-3 py-2.5">Equipment</th>
                        <th className="px-3 py-2.5">Water</th>
                        <th className="px-3 py-2.5">Operator</th>
                        <th className="px-3 py-2.5">Notes</th>
                        <th className="px-3 py-2.5">Photo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((record) => (
                        <tr key={record.id} className="border-b border-border/60 last:border-0">
                          <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTime(record.turned_at)}</td>
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-foreground">{record.windrow_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{record.windrow_row_id}</p>
                          </td>
                          <td className="px-3 py-2.5 text-foreground font-semibold whitespace-nowrap">#{record.turn_number}</td>
                          <td className="px-3 py-2.5 text-muted-foreground">{record.equipment || "-"}</td>
                          <td className="px-3 py-2.5">
                            {record.water_added ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                <Droplets size={11} />
                                Yes
                              </span>
                            ) : (
                              <span className="text-muted-foreground">No</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">{record.operator || "-"}</td>
                          <td className="px-3 py-2.5 text-muted-foreground max-w-xs truncate">{record.notes || "-"}</td>
                          <td className="px-3 py-2.5">
                            {record.photo_url ? (
                              <a
                                href={record.photo_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                <Camera size={12} /> View
                              </a>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OperatorTurnLogger() {
  const { token, user } = useAuth();
  const { isOnline, queueAction } = useOffline();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedWindrow = searchParams.get("windrow");

  const [windrows, setWindrows] = useState<WindrowOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [windrowId, setWindrowId] = useState(preselectedWindrow || "");
  const draft = useOperatorDraft(queueScope(user), "turn", windrowId, {"equipment": "", "waterAdded": false as boolean, "notes": ""});
  const equipment = draft.fields.equipment;
  const setEquipment = (value: string) => draft.setField("equipment", value);
  const waterAdded = draft.fields.waterAdded;
  const setWaterAdded = (value: boolean) => draft.setField("waterAdded", value);
  const notes = draft.fields.notes;
  const setNotes = (value: string) => draft.setField("notes", value);
  const photoFile = draft.photo;
  const setPhotoFile = draft.setPhoto;

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!photoFile) { setPhotoPreview(null); return; }
    const url = URL.createObjectURL(photoFile); setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  useEffect(() => {
    let cancelled = false;
    const scope = queueScope(user);
    const show = (rows: WindrowOption[]) => {
      if (cancelled) return;
      const active = rows.filter(w => w.status === 'active');
      setWindrows(active);
      if (!preselectedWindrow && active.length) setWindrowId(current => current || String(active[0].id));
    };
    void readOperatorRows(scope).then(rows => show(rows as WindrowOption[])).catch(() => {});
    apiFetch('/api/windrows', { headers: { Authorization: `Bearer ${token}` } })
      .then(async response => { if (!response.ok) throw new Error('Unable to load rows'); return response.json(); })
      .then(data => { show(data.windrows || []); void cacheOperatorRows(scope, data.windrows || []).catch(() => {}); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, preselectedWindrow, user?.id, user?.organization_id]);

  const handlePhotoCapture = (file: File) => {
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const removePhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!windrowId || submitting || !draft.ready) return;
    setSubmitting(true); setSubmitError(null);
    try {
      const photoBlob = photoFile ? new Blob([await photoFile.arrayBuffer()], { type: photoFile.type }) : undefined;
      await queueAction('turn', Number(windrowId), {
        waterAdded, equipment: equipment || null, operator: user?.name || user?.email || null, notes: notes || null,
      }, photoBlob);
      await draft.clear().catch(() => {});
      setSuccess(true);
      void hapticSuccess().catch(() => {});
      setTimeout(() => navigate(`/app/operator/windrows/${windrowId}`), 1500);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to save on this device. Your form is still here.');
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <Check size={36} className="text-blue-600 dark:text-blue-400" />
        </div>
        <h2 className="heading-display text-2xl text-foreground mb-2">Turn Recorded</h2>
        <p className="text-sm text-muted-foreground">
          {equipment && <><span className="font-semibold text-foreground">{equipment}</span> · </>}
          Saved on this device; upload status appears above
        </p>
        <p className="text-xs text-muted-foreground mt-2">Redirecting to windrow...</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <Link
        to="/app/operator"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"
      >
        <ArrowLeft size={14} />
        Back to Dashboard
      </Link>

      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-border bg-gradient-to-r from-blue-50 to-blue-50/50 dark:from-blue-900/10 dark:to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <RotateCcw size={22} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="heading-display text-xl text-foreground">Record Turn</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Log a windrow turning event</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6"><fieldset disabled={!!windrowId && (!draft.ready || submitting)} className="space-y-6">
          <div className="flex items-center justify-between gap-3 text-sm"><span role="status">{draft.status}</span>{draft.status && <button type="button" className="min-h-11 underline" onClick={() => { if (window.confirm('Discard this unfinished entry and its photo?')) void draft.discard().catch(() => setSubmitError('Could not discard the draft. Try again.')); }}>Discard draft</button>}</div>
          {submitError && <p role="alert" className="text-sm text-red-600">{submitError}</p>}
          <div>
            <label className="label-caps text-muted-foreground mb-2 block">
              Select Windrow *
            </label>
            {loading ? (
              <div className="h-11 bg-muted rounded-xl animate-pulse" />
            ) : (
              <select
                value={windrowId}
                onChange={(e) => setWindrowId(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              >
                <option value="">Choose a windrow...</option>
                {windrows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.row_id})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="label-caps text-muted-foreground mb-2 flex items-center gap-1.5">
              <Wrench size={11} />
              Equipment Used
            </label>
            <div className="grid grid-cols-2 gap-2">
              {EQUIPMENT_OPTIONS.map((equip) => (
                <button
                  key={equip.label}
                  type="button"
                  onClick={() => setEquipment(equipment === equip.label ? "" : equip.label)}
                  className={`flex items-center gap-2.5 px-3 py-3 text-sm font-semibold rounded-xl transition-all ${
                    equipment === equip.label
                      ? "bg-blue-600 text-white shadow-sm shadow-blue-600/20"
                      : "bg-secondary text-muted-foreground hover:text-foreground border border-border hover:border-blue-200 dark:hover:border-blue-800"
                  }`}
                >
                  <span className="text-base">{equip.icon}</span>
                  {equip.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-caps text-muted-foreground mb-2 block">
              Water Added?
            </label>
            <button
              type="button"
              onClick={() => setWaterAdded(!waterAdded)}
              className={`flex items-center gap-3 w-full px-4 py-3.5 rounded-2xl border-2 transition-all ${
                waterAdded
                  ? "border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10"
                  : "border-border bg-background hover:border-blue-200 dark:hover:border-blue-800"
              }`}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                waterAdded
                  ? "bg-blue-100 dark:bg-blue-900/30"
                  : "bg-secondary"
              }`}>
                <Droplets size={20} className={waterAdded ? "text-blue-600 dark:text-blue-400" : "text-muted-foreground/50"} />
              </div>
              <div className="text-left flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {waterAdded ? "Yes — water was added" : "No water added"}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Tap to toggle
                </p>
              </div>
              <div className={`w-12 h-6 rounded-full transition-colors relative ${
                waterAdded ? "bg-blue-500" : "bg-muted"
              }`}>
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                  waterAdded ? "translate-x-6" : "translate-x-0.5"
                }`} />
              </div>
            </button>
          </div>

          <div>
            <label className="label-caps text-muted-foreground mb-2 flex items-center gap-1.5">
              <User size={11} />
              Operator
            </label>
            <input
              type="text"
              value={user?.name || user?.email || ""}
              readOnly
              className="w-full px-3 py-2.5 bg-secondary border border-border rounded-xl text-sm text-muted-foreground"
            />
          </div>

          {photoPreview ? (
            <div>
              <label className="label-caps text-muted-foreground mb-2 block">Photo</label>
              <div className="relative">
                <img src={photoPreview} alt="Captured" className="w-full rounded-xl aspect-[4/3] object-cover" />
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute top-2 right-2 w-8 h-8 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
                >
                  x
                </button>
              </div>
            </div>
          ) : (
            <PhotoCapture
              onCapture={handlePhotoCapture}
              label="Photo (optional)"
            />
          )}

          <div>
            <label className="label-caps text-muted-foreground mb-2 block">
              Notes (optional)
            </label>
            <textarea
              rows={2}
              placeholder="Any observations..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={!windrowId || submitting}
            className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-blue-600/20"
          >
            {uploading ? "Uploading Photo..." : submitting ? "Recording..." : "Record Turn"}
          </button>
        </fieldset></form>
      </div>
    </div>
  );
}
